import {
  CLINICIAN_LABEL_CHOICES,
  CLINICIAN_LABEL_OTHER,
  CLINICIAN_REVIEW_ANSWERED_KEY_PREFIX,
  clinicianLabelValueKey,
  clinicianSeverityKey,
  severityChoicesFor,
} from "@constants/clinicianLabelConstants";
import {
  ClinicianLabel,
  ClinicianLabelField,
  ClinicianLabelInput,
  ClinicianLabelValue,
  ClinicianLabels,
  ClinicianReviewPayload,
  ClinicianSeverity,
  TestCategory,
} from "types/tests";

type TranslateFn = (key: string) => string;

export interface ClinicianLabelDraft {
  value: ClinicianLabelValue | null;
  severity: ClinicianSeverity | null;
  freeText: string;
}

export type ClinicianLabelDraftMap = Record<ClinicianLabelField, ClinicianLabelDraft>;

export const CLINICIAN_LABEL_FIELDS = Object.keys(CLINICIAN_LABEL_CHOICES) as ClinicianLabelField[];

export function wasClinicianReviewAnswered(testUuid: string): boolean {
  return sessionStorage.getItem(CLINICIAN_REVIEW_ANSWERED_KEY_PREFIX + testUuid) === "true";
}

export function rememberClinicianReviewAnswered(testUuid: string): void {
  sessionStorage.setItem(CLINICIAN_REVIEW_ANSWERED_KEY_PREFIX + testUuid, "true");
}

const EMPTY_DRAFT: ClinicianLabelDraft = { value: null, severity: null, freeText: "" };

export function formatClinicianLabel(label: ClinicianLabel | undefined, t: TranslateFn): string {
  if (!label) return "";
  const value =
    label.value === CLINICIAN_LABEL_OTHER
      ? (label.freeText ?? t(clinicianLabelValueKey(label.value)))
      : t(clinicianLabelValueKey(label.value));
  return label.severity ? `${value} (${t(clinicianSeverityKey(label.severity))})` : value;
}

export function displayClinicianLabel(
  row: { category?: string; clinicianLabels?: ClinicianLabels },
  field: ClinicianLabelField,
  t: TranslateFn,
): string {
  if (row?.category && row.category !== TestCategory.VisualField) return "";
  return formatClinicianLabel(row?.clinicianLabels?.[field], t);
}

function draftFromLabel(label?: ClinicianLabel): ClinicianLabelDraft {
  if (!label) return { ...EMPTY_DRAFT };
  return {
    value: label.value,
    severity: label.severity ?? null,
    freeText: label.freeText ?? "",
  };
}

export function draftsFromLabels(labels?: ClinicianLabels): ClinicianLabelDraftMap {
  return CLINICIAN_LABEL_FIELDS.reduce((acc, field) => {
    acc[field] = draftFromLabel(labels?.[field]);
    return acc;
  }, {} as ClinicianLabelDraftMap);
}

export function isClinicianLabelDraftComplete(draft: ClinicianLabelDraft): boolean {
  if (!draft.value) return true;
  if (draft.value === CLINICIAN_LABEL_OTHER && !draft.freeText.trim()) return false;
  return !(severityChoicesFor(draft.value).length > 0 && !draft.severity);
}

function labelInputFrom(draft: ClinicianLabelDraft): ClinicianLabelInput | null {
  if (!draft.value) return null;
  return {
    value: draft.value,
    ...(draft.severity ? { severity: draft.severity } : {}),
    ...(draft.value === CLINICIAN_LABEL_OTHER ? { freeText: draft.freeText.trim() } : {}),
  };
}

function sameLabel(input: ClinicianLabelInput | null, stored?: ClinicianLabel): boolean {
  if (!input) return !stored;
  if (!stored) return false;
  return (
    input.value === stored.value &&
    (input.severity ?? null) === (stored.severity ?? null) &&
    (input.freeText ?? null) === (stored.freeText ?? null)
  );
}

export function buildClinicianReviewPayload(
  drafts: ClinicianLabelDraftMap,
  labels: ClinicianLabels | undefined,
  comment?: { next: string; current: string },
): ClinicianReviewPayload | null {
  const payload: ClinicianReviewPayload = {};

  CLINICIAN_LABEL_FIELDS.forEach((field) => {
    const input = labelInputFrom(drafts[field]);
    if (!sameLabel(input, labels?.[field])) {
      payload[field] = input;
    }
  });

  if (comment && comment.next.trim() !== (comment.current ?? "").trim()) {
    payload.comment = comment.next.trim();
  }

  return Object.keys(payload).length > 0 ? payload : null;
}
